/**
 * TicketsPage - Página principal do módulo de Tickets
 *
 * Layout:
 * - Sidebar lateral de pipelines (expansível/recolhível)
 * - Header com métricas à direita
 * - FilterBar horizontal com busca, filtro de usuários e filtros avançados
 * - Kanban com colunas dinâmicas baseadas nos stages do pipeline selecionado
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppbarSubtitle } from '@/shared/components/layout/AppbarContext';
import { CardDensitySelector, KanbanBoard, KanbanSortSelector, rankBetween, resolveNeighborRanks } from '@/shared/components/kanban';
import { useModuleSearch } from '@/shared/components/search/ModuleSearchContext';
import {
  PageToolbar,
  ContextSelector,
  ToolbarSearch,
  ViewToggle,
  OperatorAvatarFilter,
  type OperatorAvatarFilterOperator,
  Button,
  Checkbox,
  SortableHeadCell,
  PlainHeadCell,
  SelectAllHeadCell,
  ListPagePagination,
  ListPageEmpty,
  ListPageLoading,
} from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { useIsMobile } from '@/shared/hooks';
import { PipelinesSidebar } from '@/modules/helpdesk/pipelines/components/PipelinesSidebar';
import { PipelinesSettingsDrawer } from '@/modules/helpdesk/pipelines/components/PipelinesSettingsDrawer';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks/usePipelines';
import { useStagesByPipelineId } from '@/modules/helpdesk/pipelines/hooks/useStages';
import { TicketsAdvancedFiltersComponent } from '../components/TicketsAdvancedFilters';
import { TicketCard, TicketCardAction } from '../components/TicketCard';
import { TicketDetailDrawer } from '../components/TicketDetailDrawer';
import { TicketFormDrawer } from '../components/TicketFormDrawer';
import { StageChangeConfirmation } from '../components/StageChangeConfirmation';
import { TransferTicketDialog } from '../components/TransferTicketDialog';
import { projectsService } from '@/modules/helpdesk/projects/services';
import type { TicketStatusCategory, TicketPriority } from '../types';
import { TICKET_CARD_DENSITIES, TICKET_PRIORITY } from '../types';
import { useTickets, useTicketsKanban, useTicketsPreferences, useUpdateTicket, useDeleteTicket, useCloseTicket, useTicketOperatorsDropdown } from '../hooks';
import { ticketsService, ExportTruncatedError } from '../services/ticketsService';
import { useClearStaleOperator } from '@/shared/hooks/useClearStaleOperator';
import { toOperatorFilterOperator } from '@/shared/utils/contextOperators';
import { useRetentionCount } from '@/modules/helpdesk/retention/hooks/useRetention';
import type { PopulatedTicket, TicketsAdvancedFilters } from '../types';
import type {
  TicketAlertClockConfig,
  TicketExportQuery,
  TicketKanbanCard,
  TicketKanbanQuery,
} from '@troia-v3/types';
import { TICKET_KANBAN_SORT_MODES } from '@troia-v3/types';
import {
  metricsFromAggregates,
  calculateTicketsMetrics,
  filterTicketsBySearch,
  mapScopeToDropdownFlag,
  type TicketsScope,
} from '../utils';
import { formatTimeAgo } from '@/lib/utils';
/**
 * Página de Tickets do Help Desk
 */
export const TicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  // Preferences (persisted in localStorage)
  const {
    selectedPipelineId,
    setSelectedPipelineId,
    isSidebarOpen,
    setIsSidebarOpen,
    viewMode: storedViewMode,
    setViewMode,
    sortMode,
    setSortMode,
    cardDensity,
    setCardDensity
  } = useTicketsPreferences();

  // Mobile (mockup helpdesk-tickets) só tem Kanban empilhado — não existe view Lista.
  // A preferência 'table' setada no desktop não deve renderizar a tabela de 9 colunas
  // espremida no celular: força Kanban abaixo do breakpoint `sm` (640px), o mesmo corte
  // em que o PageToolbar oculta o seg toggle Kanban/Lista.
  const isMobile = useIsMobile(639);
  const viewMode: 'kanban' | 'table' = isMobile ? 'kanban' : storedViewMode;

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  useModuleSearch({ placeholder: 'Buscar tickets…', value: searchTerm, onChange: setSearchTerm });
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [filters, setFilters] = useState<TicketsAdvancedFilters>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPipelinesSettingsOpen, setIsPipelinesSettingsOpen] = useState(false);

  // "+" central do bottom-nav mobile cria ticket nesta tela (mockup .m-add).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); setIsCreateOpen(true); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  // Table view (mockup .tbl): seleção p/ bulkbar + sort client-side + pager
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
  // Detalhe do ticket em drawer (#150) — clique no card abre slide-over
  const [detailTicketId, setDetailTicketId] = useState<string | null>(null);
  const [tableSort, setTableSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>({ key: 'title', dir: 'asc' });
  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 20;

  // Busca server-side do board com debounce (~300ms) — evita 1 request por
  // tecla. A table view continua filtrando client-side com o termo cru.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Ticket selecionado para o TransferTicketDialog (Modal M1) — aberto pelo
  // menu do card (ação "Transferir", ex-"Atribuir"). No kanban o card é o
  // TicketKanbanCard enxuto (o dialog só usa id/ticketNumber/title).
  const [transferTicket, setTransferTicket] = useState<PopulatedTicket | TicketKanbanCard | null>(null);
  // Bulk transfer (#158) — transfere os selecionados pro mesmo destino.
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);

  // Confirmação de move quando o ticket é tarefa de projeto com predecessora
  // pendente em modo 'warn' (bloqueio 'block' é enforçado pelo backend).
  // `resume` é a continuação executada na confirmação — cobre tanto o move
  // simples (onCardMove) quanto o drop posicional do modo manual (onCardDrop).
  const [pendingMove, setPendingMove] = useState<{
    fromStageName: string;
    toStageName: string;
    fromCategory: TicketStatusCategory;
    toCategory: TicketStatusCategory;
    message: string;
    resume: () => Promise<void>;
  } | null>(null);

  // Buscar pipelines (com `userScope` per-pipeline hidratado pelo backend a
  // partir de ticket-pipelines-users — ver D-context-gate, NUNCA #47).
  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelinesDropdown();

  // Badge de e-mails pendentes de decisão na fila de Retenção (Plano B, Task 8).
  const { data: retentionCount } = useRetentionCount('pending');

  // Scope do user logado no pipeline ativo. TicketsPage é per-pipeline (kanban),
  // então pega `userScope` direto do pipeline selecionado. Default `'own'`
  // (esconde filtro) quando user não tem entry em ticket-pipelines-users.
  const selectedPipelineData = useMemo(
    () => pipelinesData?.find((p) => p.id === selectedPipelineId),
    [pipelinesData, selectedPipelineId],
  );
  const userTicketsScope: TicketsScope = (selectedPipelineData?.userScope ?? 'own') as TicketsScope;

  // Subtítulo da appbar mobile (#174): "Help Desk · {pipeline}"
  useAppbarSubtitle(selectedPipelineData?.name ? `Help Desk · ${selectedPipelineData.name}` : 'Help Desk');
  const showOperatorFilter = userTicketsScope !== 'own';
  const operatorsDropdownScope = mapScopeToDropdownFlag(userTicketsScope);

  // Operadores do pipeline escolhido: quem participa dele + quem tem ticket
  // atribuído nele (mesmo desativado/excluído). `scope=team` restringe aos
  // colegas de equipe do user logado.
  const { data: operatorsData } = useTicketOperatorsDropdown({
    pipelineId: selectedPipelineId,
    ...(operatorsDropdownScope ? { scope: operatorsDropdownScope } : {}),
    enabled: showOperatorFilter,
  });

  // Auto-select first pipeline if none selected
  useEffect(() => {
    if (!selectedPipelineId && pipelinesData && pipelinesData.length > 0) {
      setSelectedPipelineId(pipelinesData[0].id);
    }
  }, [pipelinesData, selectedPipelineId, setSelectedPipelineId]);

  // Buscar stages do pipeline selecionado
  const { data: stages = [] } = useStagesByPipelineId(selectedPipelineId || undefined);

  // Buscar tickets da API filtrado por pipeline — usado só pela table view.
  // O kanban não dispara essa chamada pesada (board windowed abaixo).
  const {
    data: ticketsData,
    isLoading: listLoading,
    error: listError
  } = useTickets({
    page: 1,
    limit: 100,
    filters: selectedPipelineId ? { pipelineId: selectedPipelineId } : undefined
  }, { enabled: viewMode !== 'kanban' });

  // Filtros do board (server-side): avatar filter + subset dos filtros
  // avançados que o backend do kanban suporta (TicketKanbanFiltersSchema) —
  // priority/statusCategory são single-value (primeiro elemento, mesmo
  // critério do handleExport); stageId[]/dateRange ficam de fora (o board
  // não os aplica). MEMOIZADO pelas dependências reais: objeto novo a cada
  // render mudaria a identidade da queryKey e recriaria o `loadMore`,
  // desmontando o sentinel de scroll das colunas (bug real do SP1).
  const kanbanFilters = useMemo<TicketKanbanQuery['filters']>(() => {
    const mapped: Record<string, unknown> = {};
    const assigneeId = selectedUserId !== 'all' ? selectedUserId : filters.assigneeId;
    if (assigneeId) mapped.assigneeId = assigneeId;
    if (filters.teamId) mapped.teamId = filters.teamId;
    if (filters.category) mapped.category = filters.category;
    if (filters.priority?.[0]) mapped.priority = filters.priority[0];
    if (filters.statusCategory?.[0]) mapped.statusCategory = filters.statusCategory[0];
    if (filters.slaBreached !== undefined) mapped.slaBreached = filters.slaBreached;
    return Object.keys(mapped).length > 0 ? mapped : undefined;
  }, [selectedUserId, filters]);

  // Board kanban windowed (SP3): counts/busca/paginação por cursor vêm do
  // backend; cirurgias de cache (applyCardMove/applyCardReorder) preservam
  // o scroll das colunas nos moves.
  const {
    boardData,
    isLoading: kanbanLoading,
    error: kanbanError,
    loadMore,
    applyCardMove,
    applyCardReorder,
    invalidate: invalidateKanban,
    loadingColumns,
    errorColumns,
  } = useTicketsKanban({
    pipelineId: selectedPipelineId,
    search: debouncedSearch || undefined,
    filters: kanbanFilters,
    sortMode,
    enabled: viewMode === 'kanban',
  });

  const isLoading = viewMode === 'kanban' ? kanbanLoading : listLoading;
  const error = viewMode === 'kanban' ? kanbanError : listError;

  // Mutations
  const updateTicketMutation = useUpdateTicket();
  const deleteTicketMutation = useDeleteTicket();
  const closeTicketMutation = useCloseTicket();

  const operators: OperatorAvatarFilterOperator[] = useMemo(
    () => (operatorsData ?? []).map(toOperatorFilterOperator),
    [operatorsData],
  );

  // Trocou de pipeline e o operador escolhido não está no novo → limpa o filtro.
  useClearStaleOperator(selectedUserId === 'all' ? null : selectedUserId, operatorsData, () => setSelectedUserId('all'));

  // Todos os tickets
  const allTickets = (ticketsData?.items as PopulatedTicket[]) || [];

  // Filtrar tickets — SÓ table view (o kanban resolve busca/operador
  // server-side via useTicketsKanban).
  const filteredTickets = useMemo(() => {
    let tickets = filterTicketsBySearch(allTickets, searchTerm);

    // Filtrar por usuário
    if (selectedUserId !== 'all') {
      tickets = tickets.filter(t => t.assigneeId === selectedUserId);
    }

    return tickets;
  }, [allTickets, searchTerm, selectedUserId]);

  // Colunas do Kanban a partir do board: stages (título/cor/ordem) cruzados
  // com as janelas do backend por stageId (count real/hasMore/paginação).
  const kanbanColumns = useMemo(() => {
    if (!boardData) return [];
    const byId = new Map(boardData.columns.map((c) => [c.columnId, c]));
    return [...stages]
      .sort((a, b) => a.order - b.order)
      .map((stage) => {
        const col = byId.get(stage.id);
        const items = col?.items ?? [];
        const isClosed = stage.category === 'resolved' || stage.category === 'closed';
        // `.k-lane-sum` do mockup: encerradas → "Encerrados N"; senão SLA vencidos
        // da JANELA carregada ou "No prazo X/X".
        const breaches = items.filter((i) => i.slaBreached).length;
        const laneSummary = isClosed
          ? { label: 'Encerrados', value: String(col?.count ?? 0) }
          : breaches > 0
            ? { label: 'SLA', value: `${breaches} vencido${breaches > 1 ? 's' : ''}`, bad: true }
            : { label: 'No prazo', value: `${items.length}/${items.length}` };
        // `.k-battery`: 1 segmento por item da janela — vencido vermelho,
        // <2h âmbar, senão verde (mesmos tiers do `.sla` do card).
        const battery = !isClosed && items.length > 0
          ? items.slice(0, 14).map((i) =>
              i.slaBreached
                ? 'var(--theme-error-color)'
                : i.slaDueAt && new Date(i.slaDueAt).getTime() - Date.now() < 2 * 60 * 60 * 1000
                  ? 'var(--theme-warning-color)'
                  : 'var(--theme-success-color)'
            )
          : undefined;
        return {
          id: stage.id,
          title: stage.name,
          color: stage.color,
          items,
          count: col?.count ?? 0,
          hasMore: Boolean(col?.nextCursor),
          loadingMore: Boolean(loadingColumns[stage.id]),
          loadMoreError: Boolean(errorColumns[stage.id]),
          laneSummary,
          ...(battery ? { battery } : {}),
        };
      });
  }, [boardData, stages, loadingColumns, errorColumns]);

  // Nome da etapa por id — a linha "Etapa" do card de Detalhes. O board enxuto
  // (TicketKanbanCard) só traz stageId; o `getStageInfo` existente não serve
  // porque é tipado em PopulatedTicket.
  const stageNameById = useMemo(
    () => new Map(stages.map((s) => [s.id, s.name])),
    [stages],
  );

  // Limiares de estagnação resolvidos por etapa (cascata etapa → pipeline →
  // sistema, publicada no payload de etapas pela tarefa C4). O board é escopado
  // a UM pipeline, então o mapa cobre todas as colunas visíveis.
  const stageAlertClockById = useMemo(
    () =>
      new Map(
        stages
          .map((s) => [s.id, s.resolvedAlert?.timeInStage] as const)
          .filter((entry): entry is readonly [string, TicketAlertClockConfig] => !!entry[1]),
      ),
    [stages],
  );

  // Métricas por modo: kanban usa agregados do backend (todos os tickets
  // matched, não só as janelas), table segue calculando dos itens carregados.
  const metrics = useMemo(() => {
    if (viewMode === 'kanban' && boardData) {
      return metricsFromAggregates(boardData.aggregates);
    }
    return calculateTicketsMetrics(filteredTickets);
  }, [viewMode, boardData, filteredTickets]);

  // Métricas para sidebar
  const sidebarMetrics = useMemo(() => ({
    total: metrics.total,
    open: (metrics.byCategory['new'] || 0) + (metrics.byCategory['in_progress'] || 0),
    slaBreached: metrics.slaBreached
  }), [metrics]);

  // Handler para ações do ticket — aceita o TicketKanbanCard enxuto do board
  // (todas as ações operam por id; Transferir só usa id/número/título).
  const handleTicketAction = async (action: TicketCardAction, ticket: PopulatedTicket | TicketKanbanCard) => {
    switch (action) {
      case 'view':
        setDetailTicketId(ticket.id);
        break;
      case 'start': {
        // Move to first in_progress stage
        const inProgressStage = stages.find(s => s.category === 'in_progress');
        if (!inProgressStage) {
          toast.error('Nenhuma etapa "Em andamento" encontrada');
          return;
        }
        try {
          await updateTicketMutation.mutateAsync({ id: ticket.id, stageId: inProgressStage.id });
          toast.success('Ticket iniciado!');
        } catch {
          toast.error('Erro ao iniciar ticket');
        }
        break;
      }
      case 'assign':
        // Abre o TransferTicketDialog (Modal M1) — antes navegava pro detalhe
        setTransferTicket(ticket);
        break;
      case 'resolve': {
        const resolvedStage = stages.find(s => s.category === 'resolved');
        if (!resolvedStage) {
          toast.error('Nenhuma etapa de resolução encontrada');
          return;
        }
        try {
          await updateTicketMutation.mutateAsync({ id: ticket.id, stageId: resolvedStage.id });
          toast.success('Ticket resolvido!');
        } catch {
          toast.error('Erro ao resolver ticket');
        }
        break;
      }
      case 'close': {
        try {
          await closeTicketMutation.mutateAsync(ticket.id);
          toast.success('Ticket fechado!');
        } catch {
          toast.error('Erro ao fechar ticket');
        }
        break;
      }
      case 'delete': {
        if (!(await confirm({ message: 'Tem certeza que deseja excluir este ticket?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
        try {
          await deleteTicketMutation.mutateAsync(ticket.id);
          toast.success('Ticket excluído!');
        } catch {
          toast.error('Erro ao excluir ticket');
        }
        break;
      }
    }
  };

  // Handler para click no card — abre o drawer de detalhe (#150)
  const handleCardClick = (ticket: PopulatedTicket | TicketKanbanCard) => {
    setDetailTicketId(ticket.id);
  };

  // Executa o move do ticket no board: cirurgia otimista no cache
  // (applyCardMove — preserva o scroll das colunas, sem refetch do board) +
  // PUT /tickets/:id { stageId }. Em erro (ex.: 409 de bloqueio por
  // dependência de projeto em modo 'block'), refaz o board (invalidate
  // desfaz a cirurgia otimista) e mostra a mensagem da API.
  const doMoveTicket = async (ticket: TicketKanbanCard, fromStageId: string, toStageId: string) => {
    applyCardMove(ticket, fromStageId, toStageId);
    try {
      await ticketsService.updateTicketKanban(ticket.id, { stageId: toStageId });
      toast.success('Etapa do ticket atualizada!');
      // Invalidations existentes do fluxo de move: detalhe + timeline (o move
      // gera activity) + lista da table view. O board fica com a cirurgia local.
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticket.id] });
    } catch (err) {
      invalidateKanban();
      const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(apiMessage || 'Erro ao atualizar etapa do ticket');
    }
  };

  // Gate de dependência de projeto ANTES da mutação (fluxo preservado):
  // ticket de projeto indo para coluna in_progress/resolved/closed com
  // predecessora pendente em modo 'warn' abre a StageChangeConfirmation.
  // Retorna true quando abriu a confirmação — o caller NÃO executa a mutação
  // (a continuação `resume` roda na confirmação). Modo 'block' é enforçado
  // pelo backend (409, toast com a mensagem da API no doMoveTicket).
  const maybeGateProjectMove = async (
    ticket: TicketKanbanCard,
    fromColumnId: string,
    toColumnId: string,
    resume: () => Promise<void>,
  ): Promise<boolean> => {
    const toStage = stages.find((s) => s.id === toColumnId);
    const gatedCategories: TicketStatusCategory[] = ['in_progress', 'resolved', 'closed'];
    if (!ticket.projectId || !toStage || !gatedCategories.includes(toStage.category)) return false;

    try {
      const status = await projectsService.getTaskStatusByTicket(ticket.id);
      if (
        status.inProject &&
        status.strictestPendingMode === 'warn' &&
        status.pendingPredecessors.length > 0
      ) {
        const fromStage = stages.find((s) => s.id === fromColumnId);
        const names = status.pendingPredecessors
          .map((p) => `#${p.ticketNumber} ${p.title}`)
          .join(', ');
        setPendingMove({
          fromStageName: fromStage?.name ?? '—',
          toStageName: toStage.name,
          fromCategory: fromStage?.category ?? 'new',
          toCategory: toStage.category,
          message: `Este ticket depende de: ${names}. Essas tarefas ainda não foram concluídas. Deseja avançar mesmo assim?`,
          resume,
        });
        return true; // aguarda confirmação
      }
    } catch {
      // Falha na checagem não bloqueia o fluxo — o backend ainda protege.
    }
    return false;
  };

  // Handler para mover ticket entre colunas (stages) — drag padrão.
  const handleCardMove = async (ticket: TicketKanbanCard, fromColumnId: string, toColumnId: string) => {
    const run = () => doMoveTicket(ticket, fromColumnId, toColumnId);
    if (await maybeGateProjectMove(ticket, fromColumnId, toColumnId, run)) return;
    await run();
  };

  const confirmPendingMove = async () => {
    if (!pendingMove) return;
    const { resume } = pendingMove;
    setPendingMove(null);
    await resume();
  };

  // Dica exibida no máx. 1× por sessão ao arrastar intra-coluna fora do modo manual
  const reorderHintShown = useRef(false);
  const handleIntraColumnDropBlocked = () => {
    if (reorderHintShown.current) return;
    reorderHintShown.current = true;
    toast('Para reordenar os cards, mude a ordenação para "Personalizado".', { icon: 'ℹ️' });
  };

  // Drop posicional (modo manual): rank fractional entre os vizinhos
  // ranqueados do ponto de inserção + cirurgia de cache.
  const doDropTicket = async (
    ticket: TicketKanbanCard,
    fromColumnId: string,
    toColumnId: string,
    position: { index: number },
  ) => {
    const targetColumn = boardData?.columns.find((c) => c.columnId === toColumnId);
    if (!targetColumn) return;

    // Lista da coluna destino SEM o card movido + índice de inserção ajustado
    // (off-by-one same-column quando o card sai de posição acima do drop).
    const sameColumn = fromColumnId === toColumnId;
    const items = sameColumn ? targetColumn.items.filter((i) => i.id !== ticket.id) : targetColumn.items;
    let insertIndex = position.index;
    if (sameColumn) {
      const originalIndex = targetColumn.items.findIndex((i) => i.id === ticket.id);
      if (originalIndex !== -1 && originalIndex < position.index) insertIndex = position.index - 1;
    }

    try {
      const { aboveRank, belowRank } = resolveNeighborRanks(items, insertIndex);
      const newRank = rankBetween(aboveRank, belowRank);
      await ticketsService.updateTicketKanban(
        ticket.id,
        sameColumn ? { kanbanRank: newRank } : { stageId: toColumnId, kanbanRank: newRank },
      );
      // O card passado à cirurgia JÁ carrega o stageId novo + rank novo — o
      // hook não seta isso sozinho (armadilha/bug histórico do SP2).
      applyCardReorder(
        { ...ticket, kanbanRank: newRank, stageId: toColumnId },
        fromColumnId,
        toColumnId,
        items[insertIndex]?.id ?? null,
      );
      if (!sameColumn) {
        // Cross-column muda stage → mesmas invalidations do doMoveTicket.
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        queryClient.invalidateQueries({ queryKey: ['ticket', ticket.id] });
        queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticket.id] });
      }
    } catch (error) {
      console.error('Erro ao salvar posição do ticket:', error);
      toast.error('Não foi possível salvar a posição. Tente novamente.');
      invalidateKanban();
    }
  };

  // Drop posicional com o MESMO gate de projeto do move quando cruza coluna
  // (no modo manual o onCardDrop substitui o onCardMove também cross-column).
  const handleCardDrop = async (
    ticket: TicketKanbanCard,
    fromColumnId: string,
    toColumnId: string,
    position: { index: number },
  ) => {
    const run = () => doDropTicket(ticket, fromColumnId, toColumnId, position);
    if (fromColumnId !== toColumnId && (await maybeGateProjectMove(ticket, fromColumnId, toColumnId, run))) return;
    await run();
  };

  // Handler para exportar tickets de uma etapa (coluna do kanban) em CSV
  const handleExport = async (stageId?: string): Promise<void> => {
    const query: TicketExportQuery = {};
    if (selectedPipelineId) query.pipelineId = selectedPipelineId;
    if (stageId) query.stageId = stageId;
    if (selectedUserId !== 'all') query.assigneeId = selectedUserId;
    if (searchTerm.trim()) query.search = searchTerm.trim();
    if (filters.teamId) query.teamId = filters.teamId;
    if (filters.category) query.category = filters.category;
    if (filters.slaBreached !== undefined) query.slaBreached = filters.slaBreached;
    if (filters.priority?.[0]) query.priority = filters.priority[0];
    if (filters.dateRange?.start) query.createdFrom = filters.dateRange.start;
    if (filters.dateRange?.end) query.createdTo = filters.dateRange.end;

    try {
      await ticketsService.exportTickets(query);
      toast.success('Tickets exportados com sucesso');
    } catch (error) {
      if (error instanceof ExportTruncatedError) {
        toast.warning(error.message);
        return;
      }
      const message = error instanceof Error ? error.message : 'Falha ao exportar tickets';
      toast.error(message);
    }
  };

  // Encontrar stage info para table view
  const getStageInfo = (ticket: PopulatedTicket) => {
    const stage = stages.find(s => s.id === ticket.stageId);
    return stage || null;
  };

  return (
    <div className="h-full flex" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Sidebar de Pipelines */}
      <PipelinesSidebar
        pipelines={pipelinesData || []}
        selectedPipelineId={selectedPipelineId}
        onPipelineChange={setSelectedPipelineId}
        onSettings={() => setIsPipelinesSettingsOpen(true)}
        loading={pipelinesLoading}
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        metrics={sidebarMetrics}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar 3 zonas do mockup helpdesk-tickets (contrato DS 3.3):
            pipe-sel + ⚙ | ToolbarSearch | Operadores·Filtros·Sort·Retenção·Toggle·sep·Novo (F15).
            As métricas grandes do header antigo vivem no rodapé da sidebar Pipelines. */}
        <PageToolbar
          chain="hd-tb"
          data-testid="tickets-toolbar"
          left={
            <ContextSelector
              items={(pipelinesData || []).map((pl) => ({ id: pl.id, name: pl.name, color: pl.color }))}
              selectedId={selectedPipelineId}
              onSelect={(id) => setSelectedPipelineId(id)}
              onSettings={() => setIsPipelinesSettingsOpen(true)}
              placeholder="Selecionar pipeline"
              data-testid="tickets-pipeline-selector"
              footer={
                <button
                  type="button"
                  onClick={() => setIsPipelinesSettingsOpen(true)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-ds-base font-medium text-left"
                  style={{ color: '#8b5cf6' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid="tickets-pipeline-manage"
                >
                  <Icon name="settings" className="w-4 h-4" />
                  Gerenciar Pipelines
                </button>
              }
            />
          }
          center={
            /* Busca canônica de toolbar (F17-F22) — max-w 360, colapso via `.hd-search` */
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por título ou número…"
              shrinkClass="hd-search"
              data-testid="tickets-search-input"
            />
          }
          right={
            <>
              {/* Ordem canônica (F15): Operadores → Filtros → Sort → Toggle → sep → CTA */}
              {showOperatorFilter && (
                <OperatorAvatarFilter
                  operators={operators}
                  selectedId={selectedUserId === 'all' ? null : selectedUserId}
                  onSelect={(id) => setSelectedUserId(id ?? 'all')}
                  data-testid="tickets-operator"
                />
              )}
              {/* Filtros — <FiltersButton> canônico dentro do componente (F05) */}
              <TicketsAdvancedFiltersComponent
                variant="toolbar"
                filters={filters}
                onFiltersChange={setFilters}
              />
              {/* Ordenação — rótulo fixo "Ordenar" (F60) — só kanban */}
              {viewMode === 'kanban' && (
                <KanbanSortSelector
                  value={sortMode}
                  onChange={setSortMode}
                  modes={TICKET_KANBAN_SORT_MODES}
                />
              )}
              {/* Retenção (badge âmbar de pendentes) */}
              <button
                onClick={() => navigate('/helpdesk/tickets/retention')}
                className="relative inline-flex items-center gap-[7px] h-[38px] px-2.5 2xl:@tb:px-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex-none"
                style={{
                  border: '1px solid var(--theme-input-border)',
                  backgroundColor: 'var(--theme-card-background)',
                  color: 'var(--theme-card-text)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
                data-testid="tickets-retention-button"
              >
                <Icon name="inbox" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
                <span className="hidden 2xl:@tb:inline">Retenção</span>
                {!!retentionCount && retentionCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold"
                    style={{ backgroundColor: 'var(--theme-warning-color)', color: '#ffffff' }}
                    data-testid="tickets-retention-badge"
                  >
                    {retentionCount > 99 ? '99+' : retentionCount}
                  </span>
                )}
              </button>
              {/* Toggle de view (F09-F11): Lista primeiro, pill neutra shared */}
              <span className="inline-flex flex-none">
                <ViewToggle<'kanban' | 'table'>
                  modes={[
                    { id: 'table', icon: 'list', label: 'Lista' },
                    { id: 'kanban', icon: 'grid', label: 'Kanban' },
                  ]}
                  value={viewMode}
                  onChange={setViewMode}
                  data-testid="tickets-view"
                />
              </span>
              {/* Tamanho do card — DEPOIS do toggle (spec § 3). Legenda colapsa
                  no padrão do DS (hidden 2xl:@tb:inline, via ViewToggle).
                  `options` passa as TRÊS densidades: Detalhes (01), Normal
                  (07 "Relógio de etapa") e Compacto (05). */}
              {viewMode === 'kanban' && (
                <span className="inline-flex flex-none">
                  <CardDensitySelector
                    value={cardDensity}
                    onChange={setCardDensity}
                    options={TICKET_CARD_DENSITIES}
                    data-testid="tickets-card-density"
                  />
                </span>
              )}
              {/* separador + CTA primário único */}
              <span className="w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
              <Button
                variant="primary"
                icon="plus"
                collapsibleLabel
                className="hd-prim"
                onClick={() => setIsCreateOpen(true)}
                data-testid="new-ticket"
              >
                Novo ticket
              </Button>
            </>
          }
          mobile={
            /* `.m-tools` do mockup: busca + chips (pipeline/Operadores/Filtros/sort/Retenção) */
            <div className="w-full flex flex-col gap-2">
              <ToolbarSearch
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar ticket…"
                data-testid="tickets-search-input-mobile"
              />
              <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <span className="flex-none">
                  <ContextSelector
                    items={(pipelinesData || []).map((pl) => ({ id: pl.id, name: pl.name, color: pl.color }))}
                    selectedId={selectedPipelineId}
                    onSelect={(id) => setSelectedPipelineId(id)}
                    placeholder="Pipeline"
                    data-testid="tickets-pipeline-selector-mobile"
                  />
                </span>
                {/* Filtro de operadores mobile — chip + bottom-sheet do componente (F14) */}
                {showOperatorFilter && (
                  <OperatorAvatarFilter
                    operators={operators}
                    selectedId={selectedUserId === 'all' ? null : selectedUserId}
                    onSelect={(id) => setSelectedUserId(id ?? 'all')}
                    data-testid="tickets-operator-mobile"
                  />
                )}
                <TicketsAdvancedFiltersComponent
                  variant="toolbar"
                  filters={filters}
                  onFiltersChange={setFilters}
                />
                {/* Ordenação — rótulo fixo "Ordenar" (F60) — só kanban */}
                {viewMode === 'kanban' && (
                  <KanbanSortSelector
                    value={sortMode}
                    onChange={setSortMode}
                    modes={TICKET_KANBAN_SORT_MODES}
                  />
                )}
                <button
                  onClick={() => navigate('/helpdesk/tickets/retention')}
                  className="relative inline-flex items-center gap-1.5 h-[34px] px-3 rounded-full text-[13px] font-semibold whitespace-nowrap flex-none"
                  style={{ border: '1px solid var(--theme-input-border)', color: 'var(--theme-card-text)', backgroundColor: 'var(--theme-card-background)' }}
                  data-testid="tickets-retention-button-mobile"
                >
                  <Icon name="inbox" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
                  Retenção{!!retentionCount && retentionCount > 0 ? ` · ${retentionCount}` : ''}
                </button>
                {/* Tamanho do card. Sem gate de viewMode: abaixo de 640px a página
                    força kanban (linha 96, `isMobile ? 'kanban' : storedViewMode`),
                    e esta linha só renderiza em `sm:hidden` — o gate seria morto.
                    Mesmo `options` do desktop: as MESMAS três densidades nas duas
                    barras. */}
                <span className="inline-flex flex-none">
                  <CardDensitySelector
                    value={cardDensity}
                    onChange={setCardDensity}
                    options={TICKET_CARD_DENSITIES}
                    data-testid="tickets-card-density-mobile"
                  />
                </span>
              </div>
            </div>
          }
          below={
            /* filterbar `.hd-subbar`: só chips `.chipf` + Limpar tudo; SOME quando vazia */
            (filters.statusCategory?.length || filters.priority?.length || filters.slaBreached !== undefined) ? (
              <div
                className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
                style={{
                  backgroundColor: 'var(--theme-header-background)',
                  borderBottom: '1px solid var(--theme-header-border)',
                }}
                data-testid="tickets-filterbar"
              >
                <TicketsAdvancedFiltersComponent
                  variant="chips"
                  filters={filters}
                  onFiltersChange={setFilters}
                />
              </div>
            ) : undefined
          }
        />

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center h-full">
              <div
                className="px-4 py-3 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-error-background)',
                  borderColor: 'var(--theme-error-border)',
                  color: 'var(--theme-error-text)'
                }}
              >
                Erro ao carregar tickets. Tente novamente.
              </div>
            </div>
          )}

          {/* Kanban View */}
          {!error && viewMode === 'kanban' && (
            <KanbanBoard
              columns={kanbanColumns}
              density={cardDensity}
              renderCard={(ticket: TicketKanbanCard) => (
                <TicketCard
                  ticket={ticket}
                  onAction={handleTicketAction}
                  stageName={stageNameById.get(ticket.stageId)}
                  stageAlertClock={stageAlertClockById.get(ticket.stageId)}
                />
              )}
              onCardClick={handleCardClick}
              enableDragDrop={true}
              onCardMove={handleCardMove}
              enableReorder={sortMode === 'manual'}
              onCardDrop={handleCardDrop}
              onIntraColumnDropBlocked={handleIntraColumnDropBlocked}
              onLoadMore={loadMore}
              onExport={handleExport}
              loading={isLoading}
              emptyMessage="Nenhum ticket encontrado"
            />
          )}

          {/* Table View — `.tbl` do mockup: bulkbar lilás + tabela (cbx custom,
              stage-badge, pri pill, SLA, rowmore) + pager */}
          {!error && viewMode === 'table' && (() => {
            const sorted = [...filteredTickets];
            if (tableSort) {
              const { key, dir } = tableSort;
              sorted.sort((a, b) => {
                let cmp = 0;
                if (key === 'num') cmp = (a.ticketNumber ?? '').localeCompare(b.ticketNumber ?? '', 'pt-BR');
                else if (key === 'title') cmp = (a.title ?? '').localeCompare(b.title ?? '', 'pt-BR');
                else if (key === 'stage') cmp = (getStageInfo(a)?.name ?? '').localeCompare(getStageInfo(b)?.name ?? '', 'pt-BR');
                else if (key === 'priority') {
                  const rank: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };
                  cmp = (rank[a.priority ?? ''] ?? 0) - (rank[b.priority ?? ''] ?? 0);
                } else if (key === 'assignee') cmp = (a.assignee?.name ?? '').localeCompare(b.assignee?.name ?? '', 'pt-BR');
                else if (key === 'sla') cmp = Number(a.slaBreached ?? false) - Number(b.slaBreached ?? false);
                else cmp = new Date(a.updatedAt ?? 0).getTime() - new Date(b.updatedAt ?? 0).getTime();
                return dir === 'asc' ? cmp : -cmp;
              });
            }
            const totalPages = Math.max(1, Math.ceil(sorted.length / TABLE_PAGE_SIZE));
            const page = Math.min(tablePage, totalPages);
            const pageItems = sorted.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE);
            const allSelected = pageItems.length > 0 && pageItems.every((t) => selectedTicketIds.includes(t.id));
            const toggleAll = () => {
              setSelectedTicketIds(allSelected
                ? selectedTicketIds.filter((tid) => !pageItems.some((t) => t.id === tid))
                : [...new Set([...selectedTicketIds, ...pageItems.map((t) => t.id)])]);
            };
            const toggleOne = (tid: string) => {
              setSelectedTicketIds((prev) => prev.includes(tid) ? prev.filter((x) => x !== tid) : [...prev, tid]);
            };
            const handleSort = (key: string) => setTableSort((prev) =>
              prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
            const bulkResolve = async () => {
              const resolvedStage = stages.find((st) => st.category === 'resolved');
              if (!resolvedStage) { toast.error('Nenhuma etapa de resolução encontrada'); return; }
              try {
                for (const tid of selectedTicketIds) {
                  await updateTicketMutation.mutateAsync({ id: tid, stageId: resolvedStage.id });
                }
                toast.success(`${selectedTicketIds.length} ticket(s) resolvidos!`);
                setSelectedTicketIds([]);
              } catch { toast.error('Erro ao resolver tickets'); }
            };
            const bulkDelete = async () => {
              if (!(await confirm({ message: `Excluir ${selectedTicketIds.length} ticket(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
              try {
                for (const tid of selectedTicketIds) {
                  await deleteTicketMutation.mutateAsync(tid);
                }
                toast.success(`${selectedTicketIds.length} ticket(s) excluídos!`);
                setSelectedTicketIds([]);
              } catch { toast.error('Erro ao excluir tickets'); }
            };
            const someSelected = !allSelected && pageItems.some((t) => selectedTicketIds.includes(t.id));
            return (
              <div className="p-4">
                {/* `.bulkbar` do mockup: lilás, N selecionados + Resolver/Excluir/Limpar */}
                {selectedTicketIds.length > 0 && (
                  <div
                    className="flex items-center gap-3 rounded-lg mb-3 font-semibold"
                    style={{ padding: '9px 14px', backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                    data-testid="tickets-bulkbar"
                  >
                    <span className="text-[13px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {selectedTicketIds.length} selecionado{selectedTicketIds.length > 1 ? 's' : ''}
                    </span>
                    <span className="flex-1" />
                    <button
                      onClick={bulkResolve}
                      className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold"
                      style={{ height: 30, padding: '0 11px', border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
                      data-testid="tickets-bulk-resolve"
                    >
                      <Icon name="check-circle" size={14} />
                      Resolver
                    </button>
                    <button
                      onClick={() => setBulkTransferOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold"
                      style={{ height: 30, padding: '0 11px', border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
                      data-testid="tickets-bulk-transfer"
                    >
                      <Icon name="refresh" size={14} />
                      Transferir
                    </button>
                    <button
                      onClick={bulkDelete}
                      className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold"
                      style={{ height: 30, padding: '0 11px', border: '1px solid var(--theme-error-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-error-color)' }}
                      data-testid="tickets-bulk-delete"
                    >
                      <Icon name="trash" size={14} />
                      Excluir
                    </button>
                    <button
                      onClick={() => setSelectedTicketIds([])}
                      className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold"
                      style={{ height: 30, padding: '0 11px', border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
                      data-testid="tickets-bulk-clear"
                    >
                      <Icon name="x" size={14} />
                      Limpar
                    </button>
                  </div>
                )}

                {/* Loading/empty compartilhados (F52) */}
                {isLoading && <ListPageLoading message="Carregando tickets…" />}
                {!isLoading && pageItems.length === 0 && (
                  <ListPageEmpty
                    icon="inbox"
                    message="Nenhum ticket encontrado"
                    actionLabel="Novo ticket"
                    onAction={() => setIsCreateOpen(true)}
                  />
                )}
                {!isLoading && pageItems.length > 0 && (
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ border: '1px solid var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}
                >
                  <table className="w-full border-collapse text-sm" data-testid="tickets-table">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                        <SelectAllHeadCell
                          checked={allSelected}
                          indeterminate={someSelected}
                          onToggle={toggleAll}
                          data-testid="tickets-select-all"
                        />
                        <SortableHeadCell label="Ticket" sortKey="num" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-num" />
                        <SortableHeadCell label="Título" sortKey="title" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-title" />
                        <SortableHeadCell label="Etapa" sortKey="stage" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-stage" />
                        <SortableHeadCell label="Prioridade" sortKey="priority" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-priority" />
                        <SortableHeadCell label="Responsável" sortKey="assignee" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-assignee" />
                        <SortableHeadCell label="SLA" sortKey="sla" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-sla" />
                        <SortableHeadCell label="Atualizado" sortKey="updated" activeKey={tableSort?.key ?? null} dir={tableSort?.dir ?? 'asc'} onSort={handleSort} data-testid="tickets-th-updated" />
                        <PlainHeadCell className="w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((ticket) => {
                        const stage = getStageInfo(ticket);
                        // Fonte única de label/cor (TICKET_PRIORITY) — sem hex duplicado (F63)
                        const pri = TICKET_PRIORITY[(ticket.priority as TicketPriority)] ?? TICKET_PRIORITY.low;
                        const selected = selectedTicketIds.includes(ticket.id);
                        return (
                          <tr
                            key={ticket.id}
                            className="cursor-pointer transition-colors"
                            style={{ backgroundColor: selected ? 'var(--theme-primary-background)' : 'transparent' }}
                            onClick={() => handleCardClick(ticket)}
                            onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                            onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            data-testid={`ticket-row-${ticket.id}`}
                          >
                            <td
                              className="pl-4 pr-1 py-3"
                              style={{ borderBottom: '1px solid var(--theme-card-border)' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={selected}
                                onChange={() => toggleOne(ticket.id)}
                                aria-label="Selecionar ticket"
                                data-testid={`ticket-check-${ticket.id}`}
                              />
                            </td>
                            <td
                              className="px-4 py-3 font-bold whitespace-nowrap"
                              style={{ borderBottom: '1px solid var(--theme-card-border)', color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
                              title={`#${ticket.ticketNumber}`}
                            >
                              {/* número CURTO do mockup (#1042); completo no title/detalhe */}
                              #{String(parseInt(ticket.ticketNumber?.split('-').pop() || '', 10) || ticket.ticketNumber)}
                            </td>
                            <td className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                              <div className="font-semibold" style={{ color: 'var(--theme-text)' }}>{ticket.title}</div>
                              {(ticket.contact?.name || (ticket.tags && ticket.tags.length > 0)) && (
                                <div className="text-xs truncate max-w-[340px]" style={{ color: 'var(--theme-muted-text)', marginTop: 2 }}>
                                  {[ticket.contact?.name, ...(ticket.tags ?? [])].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                              {stage ? (
                                <span
                                  className="inline-flex items-center gap-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
                                  style={{ height: 22, padding: '0 9px', backgroundColor: `${stage.color}22`, color: stage.color }}
                                >
                                  <span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: 'currentcolor' }} />
                                  {stage.name}
                                </span>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{ticket.status || '—'}</span>
                              )}
                            </td>
                            <td className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                              <span
                                className="inline-flex items-center gap-[5px] rounded-full text-[11px] font-semibold whitespace-nowrap"
                                style={{ height: 20, padding: '0 8px', color: pri.color, backgroundColor: `${pri.color}22` }}
                              >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'currentcolor' }} />
                                {pri.label}
                              </span>
                            </td>
                            <td className="px-4 py-3" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                              {ticket.assignee ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="inline-flex items-center justify-center text-[10px] font-semibold text-white flex-none overflow-hidden"
                                    style={{ width: 24, height: 24, borderRadius: '32%', backgroundColor: '#64748b' }}
                                  >
                                    {ticket.assignee.picture
                                      ? <img src={ticket.assignee.picture} alt="" className="w-full h-full object-cover" />
                                      : ticket.assignee.name?.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                                  </span>
                                  <span style={{ color: 'var(--theme-card-text)' }}>{ticket.assignee.name}</span>
                                </span>
                              ) : (
                                <span style={{ color: 'var(--theme-card-muted, var(--theme-muted-text))' }}>Não atribuído</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                              {/* `slaTag()` do mockup: 4 estados (done · breach · warn · ok) */}
                              {(ticket.statusCategory === 'resolved' || ticket.statusCategory === 'closed') ? (() => {
                                let label = 'Fechado';
                                if (ticket.statusCategory === 'resolved') {
                                  const ms = ticket.resolvedAt ? new Date(ticket.resolvedAt).getTime() - new Date(ticket.createdAt).getTime() : 0;
                                  const dur = ms <= 0
                                    ? ''
                                    : ms < 60 * 60 * 1000
                                      ? ` em ${Math.max(1, Math.round(ms / 60000))}m`
                                      : ms < 24 * 60 * 60 * 1000
                                        ? ` em ${Math.floor(ms / 3600000)}h${Math.round((ms % 3600000) / 60000) > 0 ? `${Math.round((ms % 3600000) / 60000)}m` : ''}`
                                        : ` em ${Math.round(ms / 86400000)}d`;
                                  label = `Resolvido${dur}`;
                                }
                                return (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--theme-success-color)' }}>
                                    <Icon name="check-circle" size={13} /> {label}
                                  </span>
                                );
                              })() : ticket.slaBreached ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--theme-error-color)' }}>
                                  <Icon name="alert-circle" size={13} /> SLA vencido
                                </span>
                              ) : ticket.slaDueAt ? (() => {
                                const msLeft = new Date(ticket.slaDueAt).getTime() - Date.now();
                                const warn = msLeft > 0 && msLeft < 2 * 60 * 60 * 1000;
                                const short = msLeft <= 0
                                  ? 'agora'
                                  : msLeft < 60 * 60 * 1000
                                    ? `${Math.max(1, Math.round(msLeft / 60000))}m`
                                    : msLeft < 24 * 60 * 60 * 1000
                                      ? `${Math.floor(msLeft / 3600000)}h${Math.round((msLeft % 3600000) / 60000) > 0 ? `${Math.round((msLeft % 3600000) / 60000)}m` : ''}`
                                      : `${Math.round(msLeft / 86400000)}d`;
                                return (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: warn ? 'var(--theme-warning-color)' : 'var(--theme-muted-text)' }}>
                                    <Icon name="clock" size={13} /> SLA {short}
                                  </span>
                                );
                              })() : (
                                <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ borderBottom: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}>
                              {formatTimeAgo(new Date(ticket.updatedAt))}
                            </td>
                            <td className="py-3 pr-4 text-right" style={{ borderBottom: '1px solid var(--theme-card-border)' }} onClick={(e) => e.stopPropagation()}>
                              <TicketCard ticket={ticket} onAction={handleTicketAction} showAssignee={false} menuOnly />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}

                {/* Paginação padrão das listagens (F49) */}
                {!isLoading && sorted.length > 0 && (
                  <ListPagePagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setTablePage}
                    totalItems={sorted.length}
                    pageSize={TABLE_PAGE_SIZE}
                    itemLabel="tickets"
                  />
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Drawer de Criação de Ticket */}
      <TicketFormDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        mode="create"
        pipelineId={selectedPipelineId || undefined}
      />

      {/* Detalhe do ticket em drawer (#150) — clique no card do board */}
      <TicketDetailDrawer
        ticketId={detailTicketId}
        isOpen={detailTicketId !== null}
        onClose={() => setDetailTicketId(null)}
      />

      {/* Drawer de Configuração de Pipelines (master-detail) */}
      <PipelinesSettingsDrawer
        isOpen={isPipelinesSettingsOpen}
        onClose={() => setIsPipelinesSettingsOpen(false)}
      />

      {/* Transferir ticket — Modal M1, aberto via menu do card (ação "Transferir") */}
      <TransferTicketDialog
        ticketId={transferTicket?.id ?? ''}
        ticketNumber={transferTicket?.ticketNumber}
        ticketTitle={transferTicket?.title}
        isOpen={transferTicket !== null}
        onClose={() => setTransferTicket(null)}
        pipelineId={selectedPipelineId}
      />

      {/* Transferir em massa (#158) — mesmo dialog em modo bulk (N ids) */}
      <TransferTicketDialog
        ticketIds={selectedTicketIds}
        isOpen={bulkTransferOpen}
        onClose={() => setBulkTransferOpen(false)}
        onTransferred={() => setSelectedTicketIds([])}
        pipelineId={selectedPipelineId}
      />

      {/* Confirmação de avanço com dependência de projeto pendente (modo warn) */}
      <StageChangeConfirmation
        isOpen={pendingMove !== null}
        onClose={() => setPendingMove(null)}
        onConfirm={confirmPendingMove}
        fromStageName={pendingMove?.fromStageName ?? ''}
        toStageName={pendingMove?.toStageName ?? ''}
        fromCategory={pendingMove?.fromCategory ?? 'new'}
        toCategory={pendingMove?.toCategory ?? 'in_progress'}
        title="Dependência de projeto"
        {...(pendingMove?.message ? { message: pendingMove.message } : {})}
      />
    </div>
  );
};
